import { WorkerSelect } from "@/components/Select/WorkerSelect";
import { LabelPerson } from "@/features/person/components/LabelPerson";
import { apiClient } from "@/lib/api/client";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import {
  CFormItemBase,
  type CFormItemProps,
  buildCFormItem,
} from "../CFormItem";

type Props = CFormItemProps & {
  value?: string;
  allowEmpty?: boolean;
  readComponent?: ReactNode;
  onChange?: (personId?: string) => void;
  inputWidth?: number | string;
  inputClassName?: string;
};

/**
 * Card-based Form / PersonItem
 */
export const CFormPersonSelectItem = buildCFormItem((props: Props) => {
  const { data: selectedPerson } = useQuery({
    queryKey: ["peopleProfileData", props.value],
    queryFn: async () => {
      if (!props.value) return null;

      const personRes = await apiClient.GET("/v2/people", {
        params: { query: { personIdIn: [props.value] } },
      });

      return personRes.data?.items[0];
    },
    enabled: !!props.value,
  });

  return (
    <CFormItemBase
      {...props}
      readComponent={
        (props.readComponent ?? (props.value && selectedPerson)) ? (
          <div className="flex items-center gap-2">
            <LabelPerson
              name={selectedPerson?.name ?? ""}
              avatarUrl={selectedPerson?.avatarUrl ?? ""}
            />
          </div>
        ) : null
      }
      writeComponent={
        <WorkerSelect
          disabled={props.readonly}
          allowEmpty={props.allowEmpty}
          value={
            selectedPerson
              ? {
                  id: selectedPerson.personId,
                  name: selectedPerson.name,
                  nameInitials: selectedPerson.avatarUrl
                    ? ""
                    : (selectedPerson.name ?? "").slice(0, 2),
                  email: selectedPerson.primaryWorkerEmail ?? "",
                  avatarUrl: selectedPerson.avatarUrl ?? "",
                }
              : undefined
          }
          onChange={(person) => props.onChange?.(person?.id)}
          width={
            typeof props.inputWidth === "number" ? props.inputWidth : undefined
          }
        />
      }
    />
  );
});
